import { FC, useContext } from 'react';
import { Avatar, Box, Divider, Group, LoadingOverlay, Space, Stack, Text } from '@mantine/core';
import moment from 'moment';
import Moment from 'react-moment';
import { useParams } from 'react-router-dom';
import { useElementSize } from '@mantine/hooks';
import { MessageEventBadge } from '../../Atoms/EventBadge';
import MessagePlaceholderText from '../../Atoms/MessagePlaceholderText';
import { MessageS } from '../../Service/MessageS';
import ResourceAdapter from '../../Service/ResourceAdapter';
import { EmailBoxContext } from '../../Service/Context/EmailBoxContext';
import Attachments from '../../Organismns/Attachments';
import { ThemeText, ThemeTitle } from '../../Atoms/ThemeText';
import { MailS } from '../../Service/MailS';
import ImapAttachment from '../../Organismns/Message/ImapAttachment';
import { useEventScope } from '../../Service/hooks/useEventScope';
import MessageActions from './MessageActions';

const ChatMessage: FC<{ phoneVersion: boolean }> = ({ phoneVersion }) => {
  const { id: eventId } = useParams();
  const { isEventScope } = useEventScope();
  const { selectedMessage: message, loadingMessage } = useContext(EmailBoxContext);
  const { box } = useParams();
  const { ref, width } = useElementSize();

  if (!message) {
    return null;
  }

  const customerContact = MessageS.getCustomerContact(message);
  const isHTML = message.message && message.message.startsWith('<');

  return (
    <Box ref={ref} p={0} mt={5}>
      <Group position="apart" sx={{ alignItems: 'flex-start' }}>
        <Group noWrap ml={phoneVersion ? 0 : 'sm'} spacing="xs" sx={{ alignItems: 'flex-start' }}>
          <Avatar radius="xl" size="md">
            {MailS.getInitials(customerContact)}
          </Avatar>
          <Stack spacing={5}>
            <ThemeTitle order={6}>{customerContact.personDescription}</ThemeTitle>
            {message.subject && !message.imap && (
              <Text
                sx={(theme) => ({ maxWidth: width - 100, color: theme.colorScheme === 'dark' ? 'white' : 'black' })}
              >
                <MessagePlaceholderText email={message} value={message.subject} />
              </Text>
            )}
            {message.subject && message.imap && <ThemeText lineClamp={1}>{message.subject}</ThemeText>}
            {!message.subject && (
              <Text italic color="dimmed" weight="bolder">
                Kein Betreff
              </Text>
            )}
            <ThemeText>
              {box === 'inbox' ? 'Von: ' : 'An: '}
              <Text span color="dimmed">
                {customerContact.email}
              </Text>
            </ThemeText>
            {message.cc && message.cc.length > 0 && (
              <ThemeText mb="xs">
                CC:{' '}
                <Text span color="dimmed">
                  {message.cc.map((c) => c.email).join(',')}
                </Text>
              </ThemeText>
            )}
            {message.bcc && message.bcc.length > 0 && (
              <ThemeText mb="xs">
                BCC:{' '}
                <Text span color="dimmed">
                  {message.bcc.map((c) => c.email).join(',')}
                </Text>
              </ThemeText>
            )}
            {!isEventScope && <MessageEventBadge message={message} />}
          </Stack>
        </Group>
        {!phoneVersion && <MessageActions />}
      </Group>

      {!message.imap && (
        <ThemeText size="xs" align="right" mt={phoneVersion ? 'md' : 0}>
          {message.localDateTime && <Moment date={message.localDateTime} calendar />}
          {!message.localDateTime && `Entwurf, ${moment(message.lastModified).calendar()}`}
        </ThemeText>
      )}
      {message.imap && (
        <ThemeText size="xs" align="right">
          {message.localDateTime && <Moment date={message.localDateTime} calendar />}
          {!message.localDateTime && `Entwurf, ${moment(message.lastModified).calendar()}`}
        </ThemeText>
      )}
      <Divider my="md" />
      <Box pos="relative" sx={{ minHeight: '70vh' }}>
        <LoadingOverlay visible={loadingMessage ?? false} loaderProps={{ size: 'xl' }} />
        {message.imapAttachments &&
          message.imapAttachments.map((f) => (
            <ImapAttachment
              key={f.id ?? f.fileName}
              file={f}
              messageId={message.id ?? message.stringId}
              configId={message.outboundConfigId}
              folderName={message.fullNameFolder}
            />
          ))}
        {!message.imap && message.localDateTime ? (
          <ResourceAdapter
            serviceUrl={`${process.env.REACT_APP_FILE_SERVICE_URL}/files/messages/${message.id}`}
            component={({ data }: any) => {
              data.forEach((file: any) => {
                file.entityType = 'messages';
                file.entityId = message.id;
              });

              return (
                <Attachments
                  attachments={data}
                  eventId={undefined}
                  documentBlueprints={undefined}
                  deleteFileAttachment={undefined}
                  deleteDocumentAttachment={undefined}
                />
              );
            }}
          />
        ) : (
          <Attachments
            attachments={message.attachments}
            eventId={eventId}
            documentBlueprints={undefined}
            deleteFileAttachment={undefined}
            deleteDocumentAttachment={undefined}
          />
        )}
        {!loadingMessage && message.imap && (
          <Box mx={isHTML && phoneVersion ? -15 : 0}>
            <iframe
              title="email-body"
              srcDoc={message.message}
              style={{
                width: '100%',
                height: '90vh',
                border: 'none',
              }}
            />
          </Box>
        )}
        {!message.imap && (
          <ThemeText p="xs">
            <MessagePlaceholderText email={message} value={message.message} />
          </ThemeText>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessage;
